import Vue from "vue";
import { MiraPublishedPeriod, MiraSection } from "@/data/models/Mira";
import { buildBusinessTiles, businessTrendFormatter } from "@/utils/MiraBusinessDevelopmentView";
import { MiraTile, buildTiles, dataQualityTrendFormatter } from "@/utils/MiraDataQualityView";
import { buildDeviceTiles, devicesTrendFormatter } from "@/utils/MiraDevicesView";
import { MiraDryingContext, buildDryingTiles, dryingTrendFormatter } from "@/utils/MiraDryingView";
import { buildInsuranceTiles, insuranceTrendFormatter } from "@/utils/MiraInsuranceView";
import { formatFloat } from "@/utils/LocalisationHelper";
import { buildProcessTiles, processTrendFormatter } from "@/utils/MiraProcessFlowView";
import { formatNumber } from "@/utils/Utils";

/**
 * Each section's period tiles, built as its screen builds them, so a landing card and its settings
 * (#2610102) show what the screen leads with, in the screen's order.
 */
export const MIRA_SECTION_TILES: Record<
  MiraSection,
  (period: MiraPublishedPeriod | null, comparison: MiraPublishedPeriod | null, ctx: MiraDryingContext) => MiraTile[]
> = {
  [MiraSection.BUSINESS_DEVELOPMENT]: buildBusinessTiles,
  [MiraSection.PROCESS_FLOW]: buildProcessTiles,
  [MiraSection.HYDRY]: buildDryingTiles,
  [MiraSection.DEVICES]: buildDeviceTiles,
  [MiraSection.INSURANCE]: buildInsuranceTiles,
  [MiraSection.DATA_QUALITY]: buildTiles,
};

/**
 * Each section's value formatter, as its screen's `trendFormatter` builds it, so the landing page writes
 * a figure's Markt value in the unit the screen uses ("Markt 29 %", #2610137).
 */
export const MIRA_SECTION_FORMATTERS: Record<
  MiraSection,
  (metricId: string, ctx: MiraDryingContext, period: MiraPublishedPeriod | null) => (value: number) => string
> = {
  [MiraSection.BUSINESS_DEVELOPMENT]: businessTrendFormatter,
  [MiraSection.PROCESS_FLOW]: processTrendFormatter,
  [MiraSection.HYDRY]: dryingTrendFormatter,
  [MiraSection.DEVICES]: devicesTrendFormatter,
  [MiraSection.INSURANCE]: insuranceTrendFormatter,
  [MiraSection.DATA_QUALITY]: dataQualityTrendFormatter,
};

/** The formatting context the tile builders need, from a component's i18n and theme. */
export function miraViewContext(vm: Vue): MiraDryingContext {
  return {
    t: (key, params) => vm.$t(key, params ?? []).toString(),
    te: (key) => vm.$te(key),
    formatNumber: (value) => formatNumber(value),
    formatDecimal: (value, digits) => formatFloat(value, digits),
    dark: vm.$vuetify.theme.dark,
    locale: vm.$i18n.locale,
  };
}
